import Link from "next/link";
import styles from './singlePropertyForm.module.css';
import buttons from 'components/_styled/buttons.module.css';
import { UseClassOptions, SelectOptionsType, LeaseTypesOptions } from "../data";
import SinglePropertyDropzone from "./SinglePropertyDropzone"

import useAsyncFn from "react-use/lib/useAsyncFn";
import { useDispatch } from "react-redux";
import useReducer from "./reducer";
import { modalSet } from "_slices/view";
import { useState } from "react";
import { checkLoggedIn, getUserDetails } from "context/context";
import axios from "axios";
import { useRouter } from 'next/navigation'

import API from '@/utils/API.js'

type ErrorMessages = string[] | string;

const SinglePropertyForm = ({ }) => {
    const [{ singlePropertyForm }, dispatch] = useReducer();
    const loggedIn = checkLoggedIn();
    const user = getUserDetails();
    const reduxDispatch = useDispatch();
    const [messages, setMessages] = useState<ErrorMessages>('');
    const router = useRouter();

    const validationChecksMeth = (data) => {
        let errors: ErrorMessages = [];

        let textErrors = {
            unitname: "Unit name",
            unitaddress: "Unit address",
            description: "Description",
            leasetype: "Lease type",
            useclass: "Use class",
            size: "Size",
            rent: "Rent",
            photos: "Photos",
            contactdetails: "Contact details",
        }

        Object.keys(data).forEach((item) => {
            if (data[item] === '') {
                errors?.push(textErrors[item])
            }
        })

        if (errors.length > 0) {
            setMessages(errors)

            return false
        }
        return true;
    }

    const loopFiles = async () => {
        const data = singlePropertyForm.inputs;
        const validationChecks = validationChecksMeth(data);

        if (!validationChecks) return false;

        // Put the uploaded data into a different location to the source
        data['photos'] = await uploadFiles(singlePropertyForm.inputs.photos);
        return data;
    };


    const uploadFiles = async (files) => {
      // call the api to get the upload keys
      const newFiles: Array<string> = []
      const names = files.map(f => f.name)

      // Call the api to get upload information
      const response = await API.singleProperty.upload(names)
      .then(resp => resp.data)
      .catch(err => {
        console.log('API error', err)
      })
      
      const keys = response.data || [] 

      // Loop round the files and put them into the bucket
      for (let i = 0; i < files.length; i += 1) {
        const key = keys[i]
        const file = files[i]
        const { url, fields } = key
        const formData = new FormData()
        Object.keys(fields).forEach((k) => {
          formData.append(k, fields[k])
        })
        formData.append('file', file)
        await fetch(url, { method: 'POST', body: formData}).then((response) => {
          newFiles.push([url, fields.key].join(''))
        }).catch((err) => {
          console.error('Failed to upload', err)
        })

      }

      return newFiles
    }

    const [mailState, sendForm] = useAsyncFn(async () => {
        let issues = false;
        try {
            if (!loggedIn) {
                reduxDispatch(
                    modalSet({
                        visible: true,
                        type: "auth",
                        view: "login",
                        router
                    })
                );
                return "BAD";
            }
            setMessages('')
            const checkbox = document.getElementById('consent');
            const checkboxtc = document.getElementById('tandc');
            if (!(checkbox as HTMLInputElement).checked || !(checkboxtc as HTMLInputElement).checked) {
                setMessages("You need to have agreed to the terms before submiting the form");
                return "BAD";
            }

            const fullData = await loopFiles()
            if (!fullData) {
                return 'BAD';
            }


            // Try to send the admin email
            const response = await API.singleProperty.email({...fullData})
              .catch(err => {
                console.log('API.singleProperty.email error', err)
              })
      

            if (!response.ok || response?.data?.status !== 'OK') {
              setMessages('Sorry there was an issue submitting your property details, please try again in a while')
              return 'BAD'
            }

            // Make sure it works if there is already a ?
            const baseUrl = window.location.href.split('?', 1)[0]
            // Create return urls to this page
            const success_url = `${baseUrl}?payment=success&unitname=${fullData.unitname}`
            const cancel_url = `${baseUrl}?payment=failure`

            // Create the stripe checkout session
            const { data, status } = await API.singleProperty.stripe({ ...fullData, success_url, cancel_url })

            if (data?.url) {
                window.location = data.url
            } else {
                setMessages("Sorry there was an issue submitting your data, please check all data is correct before re-submitting");
                return "BAD";
            }

        } catch (error) {
            console.log(error)
            return "BAD";
        }
    }, [singlePropertyForm, loggedIn, user]);

    return (
        <div className={styles.FormContainer}>
            <div className={styles.FormInnerContainer}>
                <h2>Fill in your property details</h2>
                <form className={styles.SinglePropertyFormGrid} onSubmit={(e) => { e.preventDefault()}}>
                    <label className={styles.TextInputLabel}>Unit Name
                        <input className={styles.TextInputElement} placeholder="Enter full unit name" id="unitname" onChange={(e) => { dispatch({ type: "singlePropertyFormInput", payload: { unitname: e.currentTarget.value } }); }} />
                    </label>
                    <label className={styles.TextInputLabel}>Unit Address
                        <textarea className={styles.TextAreaInputElement} placeholder="Enter full unit address" id="unitaddress" onChange={(e) => { dispatch({ type: "singlePropertyFormInput", payload: { unitaddress: e.currentTarget.value } }); }} />
                    </label>
                    <label className={styles.TextInputLabel}>Description
                        <textarea className={styles.TextAreaInputElement} placeholder="Enter description" id="description" onChange={(e) => { dispatch({ type: "singlePropertyFormInput", payload: { description: e.currentTarget.value } }); }} />
                    </label>


                    <div className={styles.TwoColumnGrid}>
                        <label className={styles.TextInputLabel}>Lease Type
                            <select className={styles.TextDropDownElement} defaultValue={'DEFAULT'} id="leasetype" onChange={(e) => { dispatch({ type: "singlePropertyFormInput", payload: { leasetype: e.currentTarget.value } }); }}>
                                <option value="DEFAULT" key="default" disabled>Select lease type</option>
                                {LeaseTypesOptions.map((LeaseType: SelectOptionsType) => {
                                    return <option key={LeaseType.value} value={LeaseType.value}>{LeaseType.name}</option>
                                })}
                            </select>
                        </label>
                        <label className={styles.TextInputLabel}>Use Class
                            <select className={styles.TextDropDownElement} defaultValue="" id="useclass" onChange={(e) => { dispatch({ type: "singlePropertyFormInput", payload: { useclass: e.currentTarget.value } }); }}>
                                <option key="default" value="" disabled >Select use class</option>
                                {UseClassOptions.map((UseClass: SelectOptionsType) => {
                                    return <option key={UseClass.value} value={UseClass.value}>{UseClass.name}</option>
                                })}
                            </select>
                        </label>
                    </div>

                    <div className={styles.TwoColumnGrid}>

                        <label className={styles.TextInputLabel}>Size

                            <div className={styles.InputGroup}>
                                <input className={styles.TextInputElement} placeholder="Enter size" id="size" onChange={(e) => { dispatch({ type: "singlePropertyFormInput", payload: { size: e.currentTarget.value } }); }} />
                                <div className={styles.TextAppendElement}>
                                    <span>sq ft</span>
                                </div>
                            </div>
                        </label>

                        <label className={styles.TextInputLabel}>Rent

                            <div className={styles.InputGroup}>
                                <input className={styles.TextInputElement} id="rent" placeholder="£" onChange={(e) => { dispatch({ type: "singlePropertyFormInput", payload: { rent: e.currentTarget.value } }); }} />
                                <div className={styles.TextAppendElement}>
                                    <span>pa</span>
                                </div>
                            </div>
                        </label>

                    </div>


                    <SinglePropertyDropzone />

                    <label className={styles.TextInputLabel}>Contact details for this property listing
                        <textarea className={styles.TextAreaInputElement} placeholder="Enter contact details" id="contactdetails" onChange={(e) => { dispatch({ type: "singlePropertyFormInput", payload: { contactdetails: e.currentTarget.value } }); }} />
                    </label>

                    <input className={`${styles.InputTickBox} ${styles.checkbox}`} type="checkbox" id="consent" name="consent" value="true" />
                    <label className={styles.InputTickBoxLabel}>I acknowledge that this single listing only applies to this property. If I remove this property before the 12 months are over and want to market a new property, I will need to purchase a new single listing. </label>

                    <input className={`${styles.InputTickBox} ${styles.checkbox}`} id="tandc" type="checkbox" name="consent" value="true" />
                    <label className={styles.InputTickBoxLabel}>
                        I accept the{" "}
                        <Link
                            href="/legal/terms-and-conditions/completely-retail"
                            passHref
                            prefetch={false}
                            legacyBehavior
                        >
                            <a target="_blank"  onClick={(e) =>
                                ga.internalLinkClick("Single Property Signup Terms", "/legal/terms-and-conditions/completely-retail")
                            }>Terms and Conditions</a>
                        </Link>{" "}
                        for Completely Retail.
                    </label>

                    {messages && (
                        <div className={styles.alertDanger}>
                            {Array.isArray(messages) && (
                                <>
                                    The following data is missing:
                                    <ul>
                                        {messages.map((message, index) => {
                                            return <li key={index}>{message}</li>;
                                        })}
                                    </ul>


                                </>
                            )}

                            {
                                !Array.isArray(messages) && (
                                    <>{messages}</>
                                )
                            }
                        </div>
                    )}

                    <button className={buttons.primaryButton} onClick={(e) => { e.preventDefault(); sendForm(); }} >Checkout</button>
                </form>
            </div>
        </div>
    )
}

export default SinglePropertyForm;
